import { useState, useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Search, Sparkles, TrendingUp, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { AppLayout } from '@/components/AppLayout';
import { EmojiPackCard } from '@/components/EmojiPackCard';
import { useEmojiPacks } from '@/hooks/useEmojiPacks';
import { useEmojiListManager } from '@/hooks/useEmojiListManager';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Skeleton } from '@/components/ui/skeleton';

export default function Index() {
  useSeoMeta({
    title: 'EmojiVerse — Custom Emoji Packs on Nostr',
    description: 'Discover, create, and share custom emoji packs on the decentralized Nostr network. Express yourself without limits.',
    ogType: 'website',
    ogSiteName: 'EmojiVerse',
    ogTitle: 'EmojiVerse — Custom Emoji Packs on Nostr',
    ogDescription: 'Discover, create, and share custom emoji packs on the decentralized Nostr network. Express yourself without limits.',
    ogUrl: 'https://emojiverse.shakespeare.wtf',
    ogImage: 'https://emojiverse.shakespeare.wtf/og-image.jpg',
    ogImageWidth: '1536',
    ogImageHeight: '1024',
    ogImageAlt: 'EmojiVerse — Custom Emoji Packs on Nostr',
    twitterCard: 'summary_large_image',
    twitterTitle: 'EmojiVerse — Custom Emoji Packs on Nostr',
    twitterDescription: 'Discover, create, and share custom emoji packs on the decentralized Nostr network. Express yourself without limits.',
    twitterImage: 'https://emojiverse.shakespeare.wtf/og-image.jpg',
    twitterImageAlt: 'EmojiVerse — Custom Emoji Packs on Nostr',
  });

  const [search, setSearch] = useState('');
  const { data: packs, isLoading } = useEmojiPacks(100);
  const { isPackAdded, addPack, removePack } = useEmojiListManager();
  const { user } = useCurrentUser();

  const filtered = useMemo(() => {
    if (!packs) return [];
    if (!search.trim()) return packs;
    const q = search.toLowerCase();
    return packs.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.emojis.some((e) => e.shortcode.toLowerCase().includes(q)),
    );
  }, [packs, search]);

  return (
    <AppLayout>
      {/* Hero */}
      <div className="text-center py-12 mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-500/20 border border-violet-500/30 text-violet-300 text-sm font-medium mb-6">
          <Sparkles className="w-3.5 h-3.5" />
          Powered by Nostr
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
          Discover{' '}
          <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
            Emoji Packs
          </span>
        </h1>
        <p className="text-lg text-white/50 max-w-xl mx-auto mb-8">
          Browse, add, and share custom emoji packs on the decentralized Nostr network.
          Express yourself without limits.
        </p>

        {/* Search */}
        <div className="relative max-w-lg mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search packs, emojis, shortcodes..."
            className="pl-11 h-12 rounded-2xl bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/15 focus:border-violet-400 transition-all"
          />
        </div>
      </div>

      {/* Section title */}
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="w-5 h-5 text-violet-400" />
        <h2 className="text-lg font-semibold text-white/80">
          {search ? `Results for "${search}"` : 'Latest Packs'}
        </h2>
        {!isLoading && (
          <span className="text-sm text-white/30 ml-auto">
            {filtered.length} {filtered.length === 1 ? 'pack' : 'packs'}
          </span>
        )}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="flex items-center gap-3 mb-4">
                <Skeleton className="h-5 w-32 bg-white/10" />
                <Skeleton className="h-8 w-16 ml-auto bg-white/10 rounded-full" />
              </div>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {Array.from({ length: 8 }).map((_, j) => (
                  <Skeleton key={j} className="aspect-square rounded-xl bg-white/10" />
                ))}
              </div>
              <Skeleton className="h-5 w-20 bg-white/10 rounded-full" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🔍</div>
          <p className="text-white/50 text-lg">
            {search ? 'No packs match your search.' : 'No emoji packs found yet.'}
          </p>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="mt-3 text-violet-400 hover:text-violet-300 text-sm underline underline-offset-4"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((pack) => (
            <EmojiPackCard
              key={pack.id}
              pack={pack}
              isAdded={user ? isPackAdded(pack) : undefined}
              onAdd={user ? addPack : undefined}
              onRemove={user ? removePack : undefined}
            />
          ))}
        </div>
      )}
    </AppLayout>
  );
}
